import React from "react";

import AnnouncementIcon from "@uui/Announcement03";
import DeleteEventIcon from "@uui/CalendarMinus02";
import CheckMessageIcon from "@uui/MessageCheckSquare";
import ReviewIcon from "@uui/SearchMd";
import CancelIcon from "@uui/SlashCircle01";
import RemoveUserIcon from "@uui/UserX01";
import {
  findIndex,
  first,
  groupBy,
  isEmpty,
  map,
  partition,
  sortBy,
  upperFirst,
  values,
} from "lodash/fp";
import { rrulestr } from "rrule";

import type { SvgIconProps } from "@mui/material";

import { UseRecordingInfoData } from "@app/analytics/hooks/recordings/useRecordingInfo";
import type {
  GroupedRecommendation,
  Recommendation,
  RecommendationAction,
  RecommendationActionKind,
  RecommendationFilterAction,
  VideoClip,
} from "@app/analytics/types";
import type { CoachingVideoRecording, VideoRecording } from "@app/common/types";
import { formatStartAndEndTime } from "@app/common/utils";
import { parseUTCDateTimeFromString } from "@app/utils/utils";

export interface FilterActionLabel {
  label: string;
  icon: React.FunctionComponent<SvgIconProps>;
  color: React.CSSProperties["color"];
  backgroundColor: React.CSSProperties["backgroundColor"];
}

const FILTER_LABELS: {
  [k in RecommendationFilterAction]: FilterActionLabel;
} = {
  cancel: {
    label: "Cancel",
    icon: CancelIcon,
    color: "shades.negative.500",
    backgroundColor: "shades.negative.50",
  },
  decline: {
    label: "Decline",
    icon: DeleteEventIcon,
    color: "shades.secondary.500",
    backgroundColor: "shades.secondary.50",
  },
  remove: {
    label: "Remove",
    icon: RemoveUserIcon,
    color: "shades.orange.500",
    backgroundColor: "shades.orange.50",
  },
  review: {
    label: "Review",
    icon: ReviewIcon,
    color: "#25B3CC",
    backgroundColor: "#D4F4FA",
  },
};

const ACTION_ITEM_LABEL: FilterActionLabel = {
  label: "Action item for you",
  icon: CheckMessageIcon,
  color: "shades.primary.400",
  backgroundColor: "shades.primary.100",
};

const COACHING_LABEL: FilterActionLabel = {
  label: "Coaching",
  icon: AnnouncementIcon,
  color: "avatar.yellowGreen.dark",
  backgroundColor: "avatar.yellowGreen.main",
};

const COACHING_FADE_IN_TRANSITION_DURATION = 0.2;

export const getDefaultAction = (
  recommendation: Recommendation
): RecommendationAction => {
  const defaultAction =
    recommendation.actions.find((action) => action.default) ??
    recommendation.actions[0]!;

  return defaultAction;
};

export const getTranscriptText = (
  recommendation: Recommendation
): string | null => {
  const defaultAction = getDefaultAction(recommendation);
  if (!defaultAction.actionMetadata?.transcriptQuote) return null;
  const quote = first(defaultAction.actionMetadata.transcriptQuote);
  return quote ? `${quote?.speaker ?? "Speaker"}: "${quote.transcript}"` : null;
};

export const getVideoClip = (
  recommendation: Recommendation,
  videoRecording?: VideoRecording
): VideoClip | null => {
  if (!videoRecording) return null;

  const defaultAction = getDefaultAction(recommendation);
  const actionMetadata = defaultAction?.actionMetadata;
  if (
    isEmpty(actionMetadata) ||
    isEmpty(actionMetadata.startTime) ||
    isEmpty(actionMetadata.endTime)
  ) {
    return null;
  }

  // TODO: replace with action metadata session id once available
  const sessionId =
    defaultAction.actionMetadata?.sessionId ??
    first(recommendation.meetingContext.sessionIds);

  if (!sessionId) {
    return null;
  }

  // relative start is hardcoded for coaching recommendations
  // to create a thumbnail that isn't the fade-in transition (black screen)
  const relativeStart = isCoachingRecommendation(recommendation)
    ? COACHING_FADE_IN_TRANSITION_DURATION
    : actionMetadata.startTime.diff(videoRecording.startTime).as("seconds");
  const relativeEnd = Math.max(
    actionMetadata.endTime.diff(videoRecording.startTime).as("seconds"),
    relativeStart + 2
  );

  return {
    id: recommendation.id,
    start: relativeStart,
    end: relativeEnd,
    duration: Math.max(relativeEnd - relativeStart, 2),
  };
};

export const getAlternativeActions = (
  recommendation: Recommendation
): RecommendationAction[] => {
  return recommendation.actions.filter((action) => !action.default);
};

export const getRecommendationFilterActionFromKind = (
  action: RecommendationActionKind
): RecommendationFilterAction => {
  switch (action) {
    case "decline_invite":
      return "decline";
    case "respond_tentative":
    case "mark_optional":
    case "remove_invitee":
      return "remove";
    case "delete_event":
      return "cancel";
    case "acknowledge":
      return "review";
    default:
      return "review";
  }
};

export const getRecommendationActionIcon = (
  actionOrReco: RecommendationActionKind | Recommendation
): Omit<FilterActionLabel, "label"> => {
  if (typeof actionOrReco === "string") {
    return FILTER_LABELS[getRecommendationFilterActionFromKind(actionOrReco)];
  } else if (actionOrReco.kind === "action_item") {
    return ACTION_ITEM_LABEL;
  } else if (isCoachingRecommendation(actionOrReco)) {
    return COACHING_LABEL;
  }
  const defaultAction = getDefaultAction(actionOrReco);
  return FILTER_LABELS[
    getRecommendationFilterActionFromKind(defaultAction.kind)
  ];
};

export const parseMeetingSchedule = (
  schedule?: Recommendation["meetingContext"]["meetingSchedule"]
) => {
  if (schedule == null) return "Past Meeting";

  const startTime = parseUTCDateTimeFromString(schedule.startTime);
  const endTime = parseUTCDateTimeFromString(schedule.endTime);

  const timeString = formatStartAndEndTime(startTime, endTime);

  if (schedule.recurrence) {
    const recurrence = schedule.recurrence.map((val) => rrulestr(val).toText());
    return `${upperFirst(recurrence.join(", "))} · ${timeString}`;
  }

  return `${startTime.weekdayShort} ${startTime.toFormat(
    "M/d"
  )} · ${timeString}`;
};

const RECOMMENDATION_DESCRIPTIONS: {
  [key in RecommendationActionKind]:
    | string
    | ((recommendation: Recommendation) => string);
} = {
  acknowledge: (recommendation: Recommendation) => {
    if (recommendation.kind === "action_item") {
      return (
        first(recommendation.reasons) ??
        "Read recommends reviewing this action item"
      );
    }
    let sentenceStart = "Read recommends reviewing this meeting";
    if (recommendation.kind === "participant") {
      const whoIsIt = recommendation.label.toLowerCase().includes("your")
        ? "your"
        : "this person's";
      sentenceStart = `Read recommends reviewing ${whoIsIt} meeting metrics`;
    } else if (recommendation.meetingContext.recurringEventId) {
      sentenceStart = "Read recommends reviewing this meeting series";
    }
    return `${sentenceStart}. No action occurs when you acknowledge this recommendation.`;
  },
  remove_invitee: "Read recommends removing this person from the meeting",
  delete_event:
    "Read recommends cancelling this meeting to save time for all parties involved",
  decline_invite: "Read recommends declining this event",
  mark_optional: "Read recommends marking this person as optional",
  respond_tentative: "Read recommends responding as tentative to this event",
  adjust_start_time: "Read recommends adjusting the start time of this event",
  adjust_end_time: "Read recommends adjusting the end time of this event",
};

const RECOMMENDATION_ALTERNATIVE_DESCRIPTIONS: {
  [key in RecommendationActionKind]: string;
} = {
  acknowledge: "simply acknowledge this recommendation",
  delete_event: "delete this meeting",
  respond_tentative: "respond as tentative to the meeting invite",
  mark_optional: "mark them optional on the meeting invite",
  decline_invite: "decline the event",
  remove_invitee: "remove them from the meeting",
  adjust_start_time: "adjust the start time of this event",
  adjust_end_time: "adjust the end time of this event",
};

const concatDescriptions = (descriptions: string[]): string => {
  let sentence = descriptions.slice(0, 2).join(", but you can also ");
  if (descriptions.length > 2) {
    sentence = `${sentence} or ${descriptions.slice(2).join(" or ")}`;
  }
  return sentence.endsWith(".") ? sentence : `${sentence}.`;
};

const DEFAULT_DESCRIPTION = "Read recommends adjusting this event";
const DEFAULT_ALTERNATIVE_DESCRIPTION = "adjust this event";

export const getRecommendationDescription = (
  recommendation: Recommendation
): string => {
  const defaultAction = getDefaultAction(recommendation);

  if (isCoachingRecommendation(recommendation)) {
    // API returns the description we want to display as the first reason
    return recommendation.reasons[0];
  }

  const descriptionOrFunc =
    RECOMMENDATION_DESCRIPTIONS[defaultAction.kind] ?? DEFAULT_DESCRIPTION;

  const defaultActionDescription =
    typeof descriptionOrFunc === "string"
      ? descriptionOrFunc
      : descriptionOrFunc(recommendation);

  const alternativeActionDescriptions = getAlternativeActions(
    recommendation
  ).map(
    (action) =>
      RECOMMENDATION_ALTERNATIVE_DESCRIPTIONS[action.kind] ??
      DEFAULT_ALTERNATIVE_DESCRIPTION
  );

  return concatDescriptions([
    defaultActionDescription,
    ...alternativeActionDescriptions,
  ]);
};

export const getRecommendationReasons = (recommendation: Recommendation) =>
  // The first reason for coaching recommendations is used for the description, so it is omitted here
  isCoachingRecommendation(recommendation)
    ? recommendation.reasons.slice(1)
    : recommendation.reasons;

export const groupRecommendations = (
  recommendations: Recommendation[]
): GroupedRecommendation[] => {
  const groupedRecommendationMap = groupBy<Recommendation>(
    (rec) =>
      rec.meetingContext.recurringEventId ?? rec.meetingContext.sessionIds[0],
    recommendations
  );

  const groupedRecommendations = map(
    (groupedRecommendationList): GroupedRecommendation => {
      const defaultRecommendation = groupedRecommendationList[0];
      const meeting = defaultRecommendation.meetingContext;
      const meetingSchedule =
        meeting.meetingSchedule ??
        groupedRecommendationList.find((r) => r.meetingContext.meetingSchedule)
          ?.meetingContext.meetingSchedule;

      return {
        id: meeting.recurringEventId ?? meeting.sessionIds[0],
        meetingName: meeting.title,
        meetingSchedule,
        isRecurring: meeting.recurringEventId != null,
        recommendations: sortBy("impact", groupedRecommendationList),
        userRole: defaultRecommendation.meetingContext.role,
        inviteeCount: defaultRecommendation.meetingContext.inviteeCount ?? -1,
      };
    },
    values(groupedRecommendationMap)
  );

  const groupedCoachingRecIndex = findIndex(
    isGroupedCoachingRecommendation,
    groupedRecommendations
  );

  // shift grouped coaching recommendation to the front (pin to top of list)
  if (groupedCoachingRecIndex !== -1) {
    const groupedCoachingRec = groupedRecommendations[groupedCoachingRecIndex];

    groupedRecommendations.splice(groupedCoachingRecIndex, 1);
    groupedRecommendations.unshift(groupedCoachingRec);
  }

  return groupedRecommendations;
};

export const groupReviewedRecommendations = (
  recommendations: Recommendation[]
) => {
  const [rejectedRecommendations, notRejectedRecommendations] = partition(
    (rec) => rec.status === "rejected",
    recommendations
  );

  const [acknowledgedRecommendations, acceptedRecommendations] = partition(
    (rec) => getDefaultAction(rec).kind === "acknowledge",
    notRejectedRecommendations
  );

  return {
    accepted: acceptedRecommendations,
    acknowledged: acknowledgedRecommendations,
    rejected: rejectedRecommendations,
  };
};

// Video Recording Utils
export const getRecordingSessionId = (recommendation: Recommendation) =>
  isCoachingRecommendation(recommendation)
    ? getDefaultAction(recommendation).actionMetadata?.sessionId ?? undefined
    : first(recommendation.meetingContext.sessionIds);

export const getRecordingInfoEnabled = (recommendation: Recommendation) =>
  recommendation.kind === "action_item" ||
  isCoachingRecommendation(recommendation);

export const getSessionVideoRecording = (
  recommendation: Recommendation,
  recordingInfo?: UseRecordingInfoData
) => {
  if (isCoachingRecommendation(recommendation)) {
    return isEmpty(recordingInfo)
      ? undefined
      : getCoachingRecordingInfo(
          recommendation,
          recordingInfo.recordingAccessToken
        );
  }

  return recordingInfo && recordingInfo.status !== "unauthorized"
    ? recordingInfo
    : undefined;
};

// Coaching Recommendation Utils

export const isCoachingRecommendation = (recommendation?: Recommendation) =>
  recommendation?.label === "Coaching";

export const isGroupedCoachingRecommendation = (
  groupedRecommendation: GroupedRecommendation
) => isCoachingRecommendation(first(groupedRecommendation.recommendations));

const getCoachingRecordingInfo = (
  recommendation: Recommendation,
  recordingAccessToken: string
): CoachingVideoRecording | undefined => {
  const actionMetadata = getDefaultAction(recommendation)?.actionMetadata;

  if (
    isEmpty(actionMetadata) ||
    isEmpty(actionMetadata.playlistUrl) ||
    isEmpty(actionMetadata.recordingAccessToken)
  ) {
    return;
  }

  return {
    id: actionMetadata.id ?? "",
    playlistUrl: actionMetadata.playlistUrl,
    sessionId: actionMetadata.sessionId!, // sessionId must be defined to have gotten recordingAccessToken
    status: actionMetadata.status ?? "unavailable",
    startTime: actionMetadata.startTime!,
    recordingAccessToken,
    endTime: actionMetadata.endTime!,
    videoDuration: actionMetadata.videoDuration,
    trailerUrl: actionMetadata?.trailerUrl ?? "",
    trailerClips: actionMetadata.trailerClips ?? [],
  };
};
